import React, { useEffect, useMemo, useRef, useState } from "react";

const API_URL = window.location.origin;

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || "").trim());
}

/*
 * Slide-in "New Sender" drawer (matches the SendX reference panel).
 * - Name + Email fields
 * - Email is a searchable dropdown listing every address already created on the
 *   sending domains the user added (existing senders). Any address that is not
 *   saved yet can still be typed and added on the fly.
 * - "+ Add new domain" lets the user add a sending domain without leaving.
 * - On save the sender is created via /api/senders so it automatically appears
 *   everywhere the sender list is used (campaign builder, senders table, etc.).
 */
export default function SenderDrawer({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [senders, setSenders] = useState([]);
  const [domains, setDomains] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);

  const boxRef = useRef(null);

  async function loadAll() {
    try {
      const [sRes, dRes, mRes] = await Promise.all([
        fetch(`${API_URL}/api/senders`),
        fetch(`${API_URL}/api/domains`),
        fetch(`${API_URL}/api/mailboxes`)
      ]);
      const sData = await sRes.json();
      const dData = await dRes.json();
      const mData = await mRes.json();
      setSenders(Array.isArray(sData) ? sData : []);
      setDomains(Array.isArray(dData) ? dData : []);
      setMailboxes(Array.isArray(mData) ? mData : []);
    } catch (e) {
      /* ignore, drawer still usable */
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setEmailOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // All addresses already created on the added domains (deduped).
  const domainEmails = useMemo(() => {
    const set = new Map();
    mailboxes.forEach((m) => {
      const e = String(m.email || "").toLowerCase().trim();
      if (e) set.set(e, { email: e, name: m.displayName || m.localPart || "Sender" });
    });
    senders.forEach((s) => {
      const e = String(s.email || "").toLowerCase().trim();
      if (e && !set.has(e)) set.set(e, { email: e, name: s.name || "Sender" });
    });
    return Array.from(set.values());
  }, [mailboxes, senders]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return domainEmails;
    return domainEmails.filter(
      (o) => o.email.includes(q) || (o.name || "").toLowerCase().includes(q)
    );
  }, [domainEmails, search]);

  const typedIsNewEmail =
    isValidEmail(search) &&
    !domainEmails.some((o) => o.email === search.toLowerCase().trim());

  function pickEmail(value, senderName) {
    setEmail(value);
    if (senderName && !name.trim()) setName(senderName);
    setEmailOpen(false);
    setSearch("");
  }

  async function addDomain() {
    if (!newDomain.trim()) return;
    setAddingDomain(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/domains`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: newDomain.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Could not add domain.");
      } else {
        setNewDomain("");
        setShowAddDomain(false);
        await loadAll();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingDomain(false);
    }
  }

  async function createSender() {
    setError("");
    const finalEmail = (email || "").trim();
    if (!name.trim()) {
      setError("Please enter a sender name.");
      return;
    }
    if (!isValidEmail(finalEmail)) {
      setError("Please choose or type a valid sender email.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/senders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: finalEmail })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Sender could not be added.");
        return;
      }
      if (onCreated) onCreated(data);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="sx-drawer-overlay" onMouseDown={onClose}>
      <aside className="sx-drawer" onMouseDown={(e) => e.stopPropagation()}>
        <div className="sx-drawer-head">
          <h2>New Sender</h2>
          <button type="button" className="sx-drawer-x" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="sx-drawer-body">
          <label className="sx-drawer-label">
            Name <b>*</b>
          </label>
          <p className="sx-drawer-hint">
            This will be the name of the sender shown in messages received by recipients.
          </p>
          <input
            className="sx-drawer-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Alina from Support"
          />

          <label className="sx-drawer-label">
            Email <b>*</b>
          </label>
          <p className="sx-drawer-hint">
            This will be the email of the sender shown in messages received by recipients.
          </p>

          <div className="sx-combo" ref={boxRef}>
            <button
              type="button"
              className="sx-combo-trigger"
              onClick={() => setEmailOpen((v) => !v)}
            >
              <span className={email ? "" : "sx-combo-ph"}>
                {email || "Select or type a sender email"}
              </span>
              <span className="sx-combo-caret">⌄</span>
            </button>

            {emailOpen && (
              <div className="sx-combo-menu">
                <input
                  autoFocus
                  className="sx-combo-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search or type a new email..."
                />

                <div className="sx-combo-list">
                  {filtered.length === 0 && !typedIsNewEmail && (
                    <p className="sx-combo-empty">
                      No saved emails yet for your domains.
                    </p>
                  )}

                  {filtered.map((o) => (
                    <button
                      type="button"
                      key={o.email}
                      className="sx-combo-item"
                      onClick={() => pickEmail(o.email, o.name)}
                    >
                      <span>{o.email}</span>
                      {email === o.email && <b>✓</b>}
                    </button>
                  ))}

                  {typedIsNewEmail && (
                    <button
                      type="button"
                      className="sx-combo-add"
                      onClick={() => pickEmail(search.toLowerCase().trim())}
                    >
                      + Add &quot;{search.trim()}&quot;
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="sx-drawer-note">
            New email senders must be whitelisted before use.
          </div>

          {showAddDomain ? (
            <div className="sx-drawer-adddomain">
              <input
                className="sx-drawer-input"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="e.g. email.yourdomain.com"
              />
              <div className="sx-drawer-adddomain-actions">
                <button
                  type="button"
                  className="sx-drawer-ghost"
                  onClick={() => {
                    setShowAddDomain(false);
                    setNewDomain("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sx-drawer-ghost primary"
                  onClick={addDomain}
                  disabled={addingDomain}
                >
                  {addingDomain ? "Adding…" : "Add domain"}
                </button>
              </div>
            </div>
          ) : null}

          {error ? <div className="sx-drawer-error">{error}</div> : null}
        </div>

        <div className="sx-drawer-foot">
          <button
            type="button"
            className="sx-drawer-ghost"
            onClick={() => setShowAddDomain((v) => !v)}
          >
            + add new domain
          </button>
          <button
            type="button"
            className="sx-yellow-btn"
            onClick={createSender}
            disabled={saving}
          >
            {saving ? "Creating…" : "Create Sender"}
          </button>
        </div>
      </aside>
    </div>
  );
}
